import { Injectable, ApplicationRef } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import {jwtDecode} from "jwt-decode";
import { User } from '../models/auth.models';
import { environment } from 'src/environments/environment';
import { map } from 'rxjs/operators';
import { EventService } from './event.service';
import { Router } from '@angular/router';

@Injectable({ providedIn: 'root' })
export class AuthfakeauthenticationService {
    private currentUserSubject: BehaviorSubject<User | null>;
    public currentUser: Observable<User | null>;
    public apiStatusHandler = new Subject<any>();
    private loaderCount = 0;
    private currentstatusSubject: BehaviorSubject<User | null>;
    private permissionsSubject: BehaviorSubject<any>;
    public currentstatus: Observable<User | null>;

    constructor(private http: HttpClient, private eventService: EventService, private router: Router, private appRef: ApplicationRef) {
        this.currentUserSubject = new BehaviorSubject<User | null>(JSON.parse(localStorage.getItem('currentUser') || 'null'));
        this.currentUser = this.currentUserSubject.asObservable();
        this.currentstatusSubject = new BehaviorSubject<User | null>(JSON.parse(localStorage.getItem('currentstatus') || 'null'));
        this.currentstatus = this.currentstatusSubject.asObservable();
        this.permissionsSubject = new BehaviorSubject<any>(JSON.parse(localStorage.getItem('permissions') || 'null'));
    }

    public get currentUserValue(): User | null {
        return this.currentUserSubject.value;
    }
    public get currentstatusvalue(): any {
        return this.currentstatusSubject.value;
    }
    public get permissions(): any {
        return this.permissionsSubject.value;
    }

    private scheduleTick() {
        Promise.resolve().then(() => this.appRef.tick());
    }

    private showLoader() {
        this.loaderCount++;
        this.apiStatusHandler.next({ show: true });
    }

    private hideLoader() {
        this.loaderCount = Math.max(0, this.loaderCount - 1);
        if (this.loaderCount === 0) this.apiStatusHandler.next({ show: false });
    }

    public getApiLoaderStatus() {
        return this.apiStatusHandler.asObservable();
    }

    public setApiLoaderStatus() {
        this.loaderCount = 0;
        this.apiStatusHandler.next({ show: false });
    }

    forgot(email: string) {
        this.showLoader();
        let formData = new FormData();
        formData.append('email', email);
        return this.http.post<any>(environment.baseurl + 'auth/forgotPassword', formData)
            .pipe(map(res => {
                this.hideLoader();
                this.scheduleTick();
                return res;
            }));
    }

    resetPassword(password: string, confirm_password: string, token: string) {
        this.showLoader();
        let formData = new FormData();
        formData.append('password', password);
        formData.append('confirm_password', confirm_password);
        formData.append('token', token);
        return this.http.post<any>(environment.baseurl + 'auth/resetPassword', formData)
            .pipe(map(res => {
                this.hideLoader();
                this.scheduleTick();
                return res;
            }));
    }

    login(form: any) {
        this.showLoader();
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' })
        };
        return this.http.post<any>(environment.baseurl + 'auth/login', JSON.stringify(form), httpOptions)
            .pipe(map(res => {
                this.hideLoader();
                this.scheduleTick();
                if (res['status'] == true) {
                    let decoded_value: any = jwtDecode(res['data']);
                    decoded_value['token'] = res['data'];
                    if (res['logo_path'] != '')
                        localStorage.setItem('user_logo', res['logo_path']);
                    localStorage.setItem('currentUser', JSON.stringify(decoded_value));
                    this.currentUserSubject.next(decoded_value);
                }
                return res;
            }));
    }

    logout() {
        localStorage.clear();
        this.currentUserSubject.next(null);
        this.router.navigateByUrl('/account/login');
    }

    getFile(url: string) {
        this.showLoader();
        const httpOptions = { responseType: 'blob' as 'json' };
        return this.http.get<any>(environment.baseurl + url, httpOptions)
            .pipe(map(res => {
                this.hideLoader();
                this.scheduleTick();
                console.log(res);
                return res;
            }));
    }

    post(url: string, body: any) {
        this.showLoader();
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' })
        };
        return this.http.post<any>(environment.baseurl + url, JSON.stringify(body), httpOptions)
            .pipe(map(res => {
                this.hideLoader();
                this.scheduleTick();
                return res;
            }));
    }

    put(url: string, body: any) {
        this.showLoader();
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' })
        };
        return this.http.put<any>(environment.baseurl + url, JSON.stringify(body), httpOptions)
            .pipe(map(res => {
                this.hideLoader();
                this.scheduleTick();
                return res;
            }));
    }

    putMultipart(url: string, body: any) {
        this.showLoader();
        return this.http.put<any>(environment.baseurl + url, body)
            .pipe(map(res => {
                this.hideLoader();
                this.scheduleTick();
                return res;
            }));
    }

    postMultipart(url: string, body: any) {
        this.showLoader();
        return this.http.post<any>(environment.baseurl + url, body)
            .pipe(map(res => {
                this.hideLoader();
                this.scheduleTick();
                return res;
            }));
    }

    get(url: string, loaderstate = null) {
        console.log(url);
        if (loaderstate == null) this.showLoader();
        return this.http.get<any>(environment.baseurl + url)
            .pipe(map(res => {
                this.hideLoader();
                this.scheduleTick();
                return res;
            }));
    }

    delete(url: string) {
        this.showLoader();
        return this.http.delete<any>(environment.baseurl + url)
            .pipe(map(res => {
                this.hideLoader();
                this.scheduleTick();
                return res;
            }));
    }

    dataURLtoFile(dataurl: string, filename: string) {
        var arr = dataurl.split(','),
            mime = arr[0].match(/:(.*?);/)![1],
            bstr = atob(arr[1]),
            n = bstr.length,
            u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        return new File([u8arr], filename, { type: mime });
    }

    getPermissions() {
        return this.http.get<any>(environment.baseurl + 'admin/getUserPermissions')
            .pipe(map(res => {
                this.scheduleTick();
                if (res['status'] == true) {
                    let permission = res['data'];
                    this.permissionsSubject.next(permission);
                    localStorage.setItem('permissions', JSON.stringify(permission));
                }
                return res;
            }));
    }
}
